/**
 * 管理员权限管理相关 Store
 * 负责权限系统统计、清理、缓存管理等功能
 */

import { ref, computed } from 'vue'
import { defineStore } from 'pinia'
import { useCacheStore } from '../cache'
import { adminPermissionManagementApi } from '@/https'
import type {
    PermissionStats,
    UserPermissionSummary,
    CleanupPermissionsResponse,
    CleanupRolesResponse,
    PermissionConstraintSummary,
    UserActivityRequest,
    UserActivityResponse,
    PermissionDashboardData
} from '@/https/types_admin/permission_management'

export const useAdminPermissionManagementStore = defineStore('admin-permission-management', () => {
    const cacheStore = useCacheStore()

    // ===== 状态管理 =====
    const permissionStats = ref<PermissionStats | null>(null)
    const currentUserSummary = ref<UserPermissionSummary | null>(null)
    const constraintSummary = ref<PermissionConstraintSummary | null>(null)
    const userActivityData = ref<UserActivityResponse | null>(null)
    const lastCleanupResults = ref<{
        permissions?: CleanupPermissionsResponse
        roles?: CleanupRolesResponse
    }>({})

    // 加载状态
    const statsLoading = ref(false)
    const userSummaryLoading = ref(false)
    const constraintLoading = ref(false)
    const cleanupLoading = ref(false)
    const cacheOperationLoading = ref(false)
    const userActivityLoading = ref(false)

    // 错误状态
    const statsError = ref<string | null>(null)
    const userSummaryError = ref<string | null>(null)
    const constraintError = ref<string | null>(null)
    const cleanupError = ref<string | null>(null)
    const cacheOperationError = ref<string | null>(null)
    const userActivityError = ref<string | null>(null)

    // ===== 计算属性 =====

    // 仪表板数据
    const dashboardData = computed((): PermissionDashboardData | null => {
        if (!permissionStats.value) return null

        return {
            stats: permissionStats.value,
            recent_activities: userActivityData.value?.activities.slice(0, 10) || [],
            system_health: {
                cache_status: 'healthy', // 可以根据实际情况动态计算
                permission_consistency: 'consistent',
                expired_items_count: (lastCleanupResults.value.permissions?.cleanup_count || 0) +
                    (lastCleanupResults.value.roles?.cleanup_count || 0)
            },
            quick_actions: [
                {
                    action: 'view_stats',
                    label: '权限统计',
                    description: '查看权限系统整体统计数据',
                    enabled: true
                },
                {
                    action: 'cleanup_permissions',
                    label: '清理过期权限',
                    description: '清理已过期的权限分配',
                    enabled: true,
                    requires_confirmation: true
                },
                {
                    action: 'cleanup_roles',
                    label: '清理过期角色',
                    description: '清理已过期的角色分配',
                    enabled: true,
                    requires_confirmation: true
                },
                {
                    action: 'clear_user_cache',
                    label: '清理缓存',
                    description: '清理权限相关缓存数据',
                    enabled: true,
                    requires_confirmation: true
                }
            ]
        }
    })

    // 权限健康状态
    const permissionHealth = computed(() => {
        if (!permissionStats.value) return 'unknown'

        const expiredCount = (lastCleanupResults.value.permissions?.cleanup_count || 0) +
            (lastCleanupResults.value.roles?.cleanup_count || 0)

        if (expiredCount === 0) return 'healthy'
        if (expiredCount < 10) return 'warning'
        return 'critical'
    })

    // ===== 权限统计操作 =====

    /**
     * 加载权限系统统计
     */
    const loadPermissionStats = async (forceRefresh = false) => {
        const cacheKey = 'admin-permission-management-stats'

        if (!forceRefresh) {
            const cached = cacheStore.getCache(cacheKey)
            if (cached) {
                permissionStats.value = cached as PermissionStats
                return cached
            }
        }

        statsLoading.value = true
        statsError.value = null

        try {
            const response = await adminPermissionManagementApi.getPermissionStats()
            permissionStats.value = response

            // 缓存结果
            cacheStore.setCache(cacheKey, response, 10 * 60 * 1000) // 10分钟缓存

            return response
        } catch (err: unknown) {
            const message = err instanceof Error ? err.message : '加载权限统计失败'
            statsError.value = message
            throw err
        } finally {
            statsLoading.value = false
        }
    }

    /**
     * 加载用户权限摘要
     */
    const loadUserPermissionSummary = async (userId?: number, forceRefresh = false) => {
        const cacheKey = `admin-permission-management-user-summary-${userId || 'current'}`

        if (!forceRefresh) {
            const cached = cacheStore.getCache(cacheKey)
            if (cached) {
                currentUserSummary.value = cached as UserPermissionSummary
                return cached
            }
        }

        userSummaryLoading.value = true
        userSummaryError.value = null

        try {
            const response = await adminPermissionManagementApi.getUserPermissionSummary(userId)
            currentUserSummary.value = response

            // 缓存结果
            cacheStore.setCache(cacheKey, response, 5 * 60 * 1000) // 5分钟缓存

            return response
        } catch (err: unknown) {
            const message = err instanceof Error ? err.message : '加载用户权限摘要失败'
            userSummaryError.value = message
            throw err
        } finally {
            userSummaryLoading.value = false
        }
    }

    /**
     * 加载权限约束摘要
     */
    const loadConstraintSummary = async (forceRefresh = false) => {
        const cacheKey = 'admin-permission-management-constraints'

        if (!forceRefresh) {
            const cached = cacheStore.getCache(cacheKey)
            if (cached) {
                constraintSummary.value = cached as PermissionConstraintSummary
                return cached
            }
        }

        constraintLoading.value = true
        constraintError.value = null

        try {
            const response = await adminPermissionManagementApi.getConstraintSummary()
            constraintSummary.value = response

            // 缓存结果
            cacheStore.setCache(cacheKey, response, 15 * 60 * 1000) // 15分钟缓存

            return response
        } catch (err: unknown) {
            const message = err instanceof Error ? err.message : '加载权限约束摘要失败'
            constraintError.value = message
            throw err
        } finally {
            constraintLoading.value = false
        }
    }

    // ===== 清理操作 =====

    /**
     * 清理过期权限
     */
    const cleanupExpiredPermissions = async (dryRun = true) => {
        cleanupLoading.value = true
        cleanupError.value = null

        try {
            const response = await adminPermissionManagementApi.cleanupExpiredPermissions({ dry_run: dryRun })

            // 保存清理结果
            lastCleanupResults.value.permissions = response

            // 如果是实际清理，清除相关缓存
            if (!dryRun) {
                clearManagementCache()
            }

            return response
        } catch (err: unknown) {
            const message = err instanceof Error ? err.message : '清理过期权限失败'
            cleanupError.value = message
            throw err
        } finally {
            cleanupLoading.value = false
        }
    }

    /**
     * 清理过期角色分配
     */
    const cleanupExpiredRoles = async (dryRun = true) => {
        cleanupLoading.value = true
        cleanupError.value = null

        try {
            const response = await adminPermissionManagementApi.cleanupExpiredRoles({ dry_run: dryRun })

            // 保存清理结果
            lastCleanupResults.value.roles = response

            // 如果是实际清理，清除相关缓存
            if (!dryRun) {
                clearManagementCache()
            }

            return response
        } catch (err: unknown) {
            const message = err instanceof Error ? err.message : '清理过期角色失败'
            cleanupError.value = message
            throw err
        } finally {
            cleanupLoading.value = false
        }
    }

    // ===== 缓存操作 =====

    /**
     * 清理用户缓存
     */
    const clearUserCache = async (userId?: number) => {
        cacheOperationLoading.value = true
        cacheOperationError.value = null

        try {
            const response = await adminPermissionManagementApi.clearUserCache(
                userId ? { user_id: userId } : {}
            )

            // 清除本地相关缓存
            if (userId) {
                cacheStore.removeCacheByPrefix(`admin-permission-management-user-summary-${userId}`)
            } else {
                cacheStore.removeCacheByPrefix('admin-permission-management-user-summary-current')
            }

            return response
        } catch (err: unknown) {
            const message = err instanceof Error ? err.message : '清理用户缓存失败'
            cacheOperationError.value = message
            throw err
        } finally {
            cacheOperationLoading.value = false
        }
    }

    /**
     * 清理角色缓存
     */
    const clearRoleCache = async (roleId: number) => {
        cacheOperationLoading.value = true
        cacheOperationError.value = null

        try {
            const response = await adminPermissionManagementApi.clearRoleCache({ role_id: roleId })

            // 清除本地相关缓存
            clearManagementCache()

            return response
        } catch (err: unknown) {
            const message = err instanceof Error ? err.message : '清理角色缓存失败'
            cacheOperationError.value = message
            throw err
        } finally {
            cacheOperationLoading.value = false
        }
    }

    // ===== 用户活动 =====

    /**
     * 加载用户活动记录
     */
    const loadUserActivity = async (params: UserActivityRequest, forceRefresh = false) => {
        const cacheKey = `admin-permission-management-activity-${JSON.stringify(params)}`

        if (!forceRefresh) {
            const cached = cacheStore.getCache(cacheKey)
            if (cached) {
                userActivityData.value = cached as UserActivityResponse
                return cached
            }
        }

        userActivityLoading.value = true
        userActivityError.value = null

        try {
            const response = await adminPermissionManagementApi.getUserActivity(params)
            userActivityData.value = response

            // 缓存结果
            cacheStore.setCache(cacheKey, response, 3 * 60 * 1000) // 3分钟缓存

            return response
        } catch (err: unknown) {
            const message = err instanceof Error ? err.message : '加载用户活动记录失败'
            userActivityError.value = message
            throw err
        } finally {
            userActivityLoading.value = false
        }
    }

    // ===== 工具方法 =====

    /**
     * 清除权限管理相关缓存
     */
    const clearManagementCache = () => {
        cacheStore.removeCacheByPrefix('admin-permission-management-')
    }

    /**
     * 重置状态
     */
    const resetManagementState = () => {
        permissionStats.value = null
        currentUserSummary.value = null
        constraintSummary.value = null
        userActivityData.value = null
        lastCleanupResults.value = {}

        // 重置错误状态
        statsError.value = null
        userSummaryError.value = null
        constraintError.value = null
        cleanupError.value = null
        cacheOperationError.value = null
        userActivityError.value = null

        // 重置加载状态
        statsLoading.value = false
        userSummaryLoading.value = false
        constraintLoading.value = false
        cleanupLoading.value = false
        cacheOperationLoading.value = false
        userActivityLoading.value = false
    }

    /**
     * 刷新所有数据
     */
    const refreshAllData = async () => {
        await Promise.allSettled([
            loadPermissionStats(true),
            loadConstraintSummary(true),
            loadUserPermissionSummary(undefined, true)
        ])
    }

    return {
        // 状态
        permissionStats,
        currentUserSummary,
        constraintSummary,
        userActivityData,
        lastCleanupResults,

        // 加载状态
        statsLoading,
        userSummaryLoading,
        constraintLoading,
        cleanupLoading,
        cacheOperationLoading,
        userActivityLoading,

        // 错误状态
        statsError,
        userSummaryError,
        constraintError,
        cleanupError,
        cacheOperationError,
        userActivityError,

        // 计算属性
        dashboardData,
        permissionHealth,

        // 方法
        loadPermissionStats,
        loadUserPermissionSummary,
        loadConstraintSummary,
        cleanupExpiredPermissions,
        cleanupExpiredRoles,
        clearUserCache,
        clearRoleCache,
        loadUserActivity,
        clearManagementCache,
        resetManagementState,
        refreshAllData
    }
})
